///
/// EarthBrowser
///
/// Created by Matt Giger on 9/7/2026
/// Copyright (c) 2026 Geodata Labs
///
/// House "support EarthBrowser" interstitial — the shareware-style nag,
/// gated by the conversion ladder (engagementSlice): it appears only at
/// rung 2 (4+ sessions of use, or 7+ days of tenure), at most once per
/// day, and never on a session that arrived via a shared deep link. The
/// dismiss delay escalates with lifetime showings (immediately dismissible
/// at first, then 5 seconds more per showing, capped at 20). The backdrop
/// blurs the app and blocks all interaction until dismissed. An undismissed
/// interstitial survives page refreshes. Never renders for paid subscribers
/// or during beta.
///
/// Signed-out users get a "create free account" CTA (registration is one
/// click via OAuth); signed-in free users get the subscribe CTA.

import { SupportPitch, supportCtaButtonClass } from '@/components/shared/pricing/SupportPitch'
import { Button } from '@/components/ui/button'
import { getBetaStatus } from '@/hooks/useBetaStatus'
import { useEntitlements } from '@/hooks/useEntitlements'
import { setAuthOpen, setSubscribeOpen } from '@/redux/dialogSlice'
import {
	conversionRung,
	dismissInterstitial,
	localDay,
	recordInterstitialShown,
	signinWallActive,
} from '@/redux/engagementSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { PricingPlans } from '@shared-types/pricing-plans'
import { track } from '@/utils/analytics'
import { XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

// Let the user engage with the globe before the interruption: this is a
// pause-point delay, not an ambush (the ladder already guarantees this is
// at least their fourth session or eighth day)
const LAUNCH_DELAY_MS = 60_000
// How often to check eligibility
const CHECK_INTERVAL_MS = 15_000

const appLoadTime = Date.now()

export function SupportInterstitial() {
	const dispatch = useAppDispatch()
	const userProfile = useAppSelector(state => state.user.userProfile)
	const engagement = useAppSelector(state => state.engagement)
	// Canonical paid rule (yearly / beta_yearly, active or trialing) so nag
	// suppression always agrees with the Phase 4 depth gates
	const { isPaid: hasPaidAccess, isLoading } = useEntitlements()

	const { isBeta } = getBetaStatus()

	// The dev-signin test account (api-server/src/user/dev-signin.ts) should
	// never see the nag during local development
	const isDevTestUser = import.meta.env.DEV && userProfile?.email === 'dev@localhost.test'

	const [open, setOpen] = useState(false)
	// Countdown is derived from a deadline timestamp rather than decremented
	// per tick, so background-tab timer throttling can't freeze it.
	const [dismissAt, setDismissAt] = useState(0)
	const [countdown, setCountdown] = useState(0)

	const plan = PricingPlans.yearly

	// Ladder gate: rung 2 only (which also excludes deep-link sessions).
	// Once the sign-in wall is active for a signed-out visitor it owns the
	// screen, so the interstitial stands down.
	const rung = conversionRung(engagement)
	const wallActive = !userProfile && signinWallActive(engagement)
	const eligible = !isBeta && !isDevTestUser && rung === 2 && !wallActive &&
		!(userProfile && (isLoading || hasPaidAccess))

	// Single open path: recordInterstitialShown arms interstitialPendingDelay
	// in the slice and this effect opens the dialog from it. Because the
	// pending delay is persisted and only cleared on dismissal, an
	// undismissed interstitial reopens after a refresh with its full
	// countdown — reloading is strictly slower than clicking dismiss.
	useEffect(() => {
		if (!eligible || open) return
		const pendingDelay = engagement.interstitialPendingDelay
		if (pendingDelay === null) return
		setDismissAt(Date.now() + pendingDelay * 1000)
		setCountdown(pendingDelay)
		setOpen(true)
	}, [eligible, open, engagement.interstitialPendingDelay])

	// Periodically check whether it's time to show: once per day, after the
	// user has had time to engage with the globe
	useEffect(() => {
		if (!eligible || open) return
		if (engagement.interstitialLastShownDay === localDay(new Date())) return
		const timer = setInterval(() => {
			if (Date.now() - appLoadTime < LAUNCH_DELAY_MS) return
			if (engagement.interstitialLastShownDay === localDay(new Date())) return
			const showCount = engagement.interstitialShowCount + 1
			track('support_interstitial_shown', {
				session_count: engagement.sessionCount,
				show_count: showCount,
				dismiss_delay_s: Math.min((showCount - 1) * 5, 20),
				rung,
			})
			dispatch(recordInterstitialShown())
		}, CHECK_INTERVAL_MS)
		return () => clearInterval(timer)
	}, [eligible, open, engagement.interstitialLastShownDay, engagement.interstitialShowCount, engagement.sessionCount, rung, userProfile, dispatch])

	// Recompute the remaining dismiss delay from the deadline. Ticks every
	// 500ms and on tab-visibility changes, so returning to a throttled
	// background tab immediately shows the true (usually elapsed) state.
	useEffect(() => {
		if (!open) return
		const update = () => {
			setCountdown(Math.max(0, Math.ceil((dismissAt - Date.now()) / 1000)))
		}
		update()
		const timer = setInterval(update, 500)
		document.addEventListener('visibilitychange', update)
		return () => {
			clearInterval(timer)
			document.removeEventListener('visibilitychange', update)
		}
	}, [open, dismissAt])

	const handleDismiss = () => {
		dispatch(dismissInterstitial())
		setOpen(false)
	}

	const handleCta = () => {
		handleDismiss()
		track('support_interstitial_cta_clicked', { signed_in: !!userProfile, rung })
		// Checkout needs an account: signed-out users register first
		dispatch(userProfile ? setSubscribeOpen(true) : setAuthOpen(true))
	}

	if (!open || !eligible) {
		return null
	}

	const canDismiss = countdown <= 0

	return (
		// Backdrop blurs the app and swallows every click/scroll until dismissed
		<div
			className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[2px] animate-in fade-in duration-300"
			onClick={(event) => event.stopPropagation()}
		>
			<div className="relative max-w-lg w-full mx-4">
				<div className="bg-background/60 backdrop-blur-xl border border-border/50 rounded-3xl shadow-2xl overflow-hidden">
					<SupportPitch
						extraEntitlements={['No more of this window, ever']}
						actions={
							<>
								<Button onClick={handleCta} className={supportCtaButtonClass}>
									{userProfile
										? `Subscribe — $${plan.price.toFixed(2)}/${plan.interval}`
										: 'Create free account — one click'}
								</Button>

								{canDismiss ? (
									<Button variant="ghost" onClick={handleDismiss} className="rounded-full text-muted-foreground">
										<XIcon className="h-4 w-4 mr-1" />
										Maybe later
									</Button>
								) : (
									<div className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50">
										<div className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
										<span className="text-sm text-muted-foreground">Continue in {countdown}s…</span>
									</div>
								)}
							</>
						}
					/>
				</div>
			</div>
		</div>
	)
}
